// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DatadogErrorTrackingService } from '@lfx/core/services/datadog-error-tracking.service';

/**
 * HTTP Interceptor that automatically captures and sends HTTP errors to Datadog RUM
 *
 * Features:
 * - Captures all 4xx and 5xx HTTP errors automatically
 * - Infers feature name from URL patterns
 * - Measures request duration
 * - Sanitizes URLs to remove sensitive data
 * - Skips certain URLs that should not be tracked (health checks, etc.)
 *
 * @example
 * The interceptor is registered in the app module:
 * ```typescript
 * providers: [
 *   {
 *     provide: HTTP_INTERCEPTORS,
 *     useClass: DatadogHttpErrorInterceptor,
 *     multi: true,
 *   },
 * ]
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DatadogHttpErrorInterceptor implements HttpInterceptor {
  /**
   * URLs that should be excluded from error tracking
   */
  private readonly excludedUrls: string[] = [
    '/health',
    '/ping',
    '/favicon.ico',
    'analytics',
    'segment',
    'intercom',
  ];

  constructor(private errorTracking: DatadogErrorTrackingService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip tracking for excluded URLs
    if (this.shouldExcludeUrl(req.url)) {
      return next.handle(req);
    }

    const startTime = Date.now();

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const duration = Date.now() - startTime;

        // Only track actual HTTP errors (4xx, 5xx)
        if (error instanceof HttpErrorResponse && error.status >= 400) {
          this.trackHttpError(req, error, duration);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Track the HTTP error to Datadog
   */
  private trackHttpError(
    req: HttpRequest<unknown>,
    error: HttpErrorResponse,
    duration: number
  ): void {
    const feature = this.errorTracking.inferFeatureFromUrl(req.url);

    this.errorTracking.trackHttpError(error, {
      feature,
      action: this.inferAction(req),
      httpUrl: req.url,
      httpMethod: req.method,
      httpDuration: duration,
    });
  }

  /**
   * Infer the action from the HTTP method and URL
   */
  private inferAction(req: HttpRequest<unknown>): string {
    const method = req.method.toUpperCase();
    const url = req.url.toLowerCase();

    // Map common patterns to action names
    if (url.includes('/workspaces')) {
      if (method === 'POST' && !url.includes('/projects')) {
        return 'create-workspace';
      }
      if (method === 'DELETE' && url.includes('/projects/')) {
        return 'remove-project';
      }
      if (method === 'DELETE') {
        return 'delete-workspace';
      }
      if (method === 'PUT') {
        return 'update-workspace';
      }
      if (url.includes('/projects') && method === 'POST') {
        return 'add-projects';
      }
    }

    if (url.includes('/key-projects')) {
      return 'load-projects';
    }

    if (url.includes('/cdp/projects/search')) {
      return 'search-projects';
    }

    if (url.includes('/cdp/project/')) {
      return 'get-project-details';
    }

    // Default action based on HTTP method
    switch (method) {
      case 'GET':
        return 'fetch';
      case 'POST':
        return 'create';
      case 'PUT':
      case 'PATCH':
        return 'update';
      case 'DELETE':
        return 'delete';
      default:
        return 'request';
    }
  }

  /**
   * Check if the URL should be excluded from tracking
   */
  private shouldExcludeUrl(url: string): boolean {
    const urlLower = url.toLowerCase();
    return this.excludedUrls.some(excluded => urlLower.includes(excluded));
  }
}

